import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { FadeUp } from "@/components/Reveal";

const CHAPTERS = [
  {
    n: "01",
    title: "Precision",
    kicker: "Stop paying for rework",
    body: "The TX73 computes bucket-tip position in real time against the design model — 10 mm horizontal, 15 mm vertical. No guesswork, no over-excavation, no paying twice for the same dirt.",
    img: "https://images.unsplash.com/photo-1603816886392-25f1d28406d9?crop=entropy&cs=srgb&fm=jpg&q=85",
    alt: "Excavator bucket digging",
  },
  {
    n: "02",
    title: "Speed",
    kicker: "Digging the same morning",
    body: "Mount in under two hours. Calibrate in thirty minutes. First cut the same day. Less time setting up means more time digging — and every operator sees live cut/fill on the display. Our EasyNAV system can be installed in under 2 hours.",
    img: "https://images.unsplash.com/photo-1613928270169-4e4462c75a79?crop=entropy&cs=srgb&fm=jpg&q=85",
    alt: "Yellow excavator shaping grade on brown soil",
  },
  {
    n: "03",
    title: "Compatibility",
    kicker: "Fits the machines you already run",
    body: (
      <>
        Swing boom, triple boom, tilt bucket, offset boom — the EMG100 fits them all. The{" "}
        <Link
          to="/products/3d"
          data-testid="manifesto-tx73-link"
          className="text-signal underline decoration-signal/40 underline-offset-4 transition-colors hover:text-neon"
        >
          TX73
        </Link>{" "}
        adds automated tilt control for Engcon and Steelwrist tiltrotators. If it digs, we can guide it.
      </>
    ),
    img: "https://images.unsplash.com/photo-1534097575056-ddba81f714c8?crop=entropy&cs=srgb&fm=jpg&q=85",
    alt: "Aerial view of excavator and dump truck",
  },
  {
    n: "04",
    title: "Control",
    kicker: "One screen for the whole fleet",
    body: "iSite cloud keeps every machine current with over-the-air updates, remote troubleshooting and data sync — while as-built views make progress visible to the office, not just the cab.",
    img: "/mcsite.png",
    alt: "CHCNAV MCSite cloud platform on a laptop screen",
    screen: true,
  },
  {
    n: "05",
    title: "About Green Grade",
    kicker: "The crew behind the crosshair",
    body: "We're the authorized CHCNAV distributor for machine control — and we don't just sell boxes. We spec the right system for your fleet, install it on your machines, train your operators and back you with local support, long after the first dig.",
    cta: { to: "/precision-solutions", label: "Learn about CHCNAV", testid: "manifesto-chcnav-link" },
    img: "https://images.unsplash.com/photo-1652303518379-c0ef1c9fb2b1?crop=entropy&cs=srgb&fm=jpg&q=85",
    alt: "Green Grade team in front of an excavator on site",
  },
];

export default function Manifesto() {
  return (
    <section data-testid="manifesto-section" className="px-4 py-20 sm:px-8 lg:px-16 lg:py-32">
      <div className="mx-auto max-w-[1600px]">
        <div className="space-y-24 lg:space-y-32">
          {CHAPTERS.map((c, i) => (
            <div
              key={c.n}
              className={`grid items-center gap-10 lg:grid-cols-12 lg:gap-16 ${
                i % 2 === 1 ? "lg:[direction:rtl]" : ""
              }`}
            >
              <div className="lg:col-span-2 lg:[direction:ltr]">
                <FadeUp>
                  <span className="text-stroke font-display text-7xl font-black leading-none sm:text-8xl">{c.n}</span>
                </FadeUp>
              </div>
              <div className="lg:col-span-5 lg:[direction:ltr]">
                <FadeUp delay={0.08}>
                  <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-signal">{c.kicker}</p>
                  <h3 className="mt-4 font-display text-4xl font-black uppercase tracking-tight text-white sm:text-5xl">
                    {c.title}
                  </h3>
                  <p className="mt-6 max-w-lg text-base leading-relaxed text-fog sm:text-lg">{c.body}</p>
                  {c.cta && (
                    <Link
                      to={c.cta.to}
                      data-testid={c.cta.testid}
                      className="group mt-8 inline-flex items-center gap-3 border border-signal/50 px-6 py-3 font-mono text-xs uppercase tracking-[0.25em] text-neon transition-colors hover:bg-signal/10"
                    >
                      {c.cta.label}
                      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                  )}
                </FadeUp>
              </div>
              <div className="lg:col-span-5 lg:[direction:ltr]">
                <FadeUp delay={0.16}>
                  {c.screen ? (
                    <div className="group" data-testid="laptop-mockup">
                      <div className="rounded-t-xl border-4 border-b-0 border-slate-700 bg-slate-800 p-2">
                        <img
                          src={c.img}
                          alt={c.alt}
                          loading="lazy"
                          className="aspect-[16/9] w-full rounded-sm object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]"
                        />
                      </div>
                      <div className="h-3 w-full rounded-b-xl bg-slate-700 shadow-lg" />
                      <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-fog">
                        iSite cloud — live on screen
                      </p>
                    </div>
                  ) : (
                    <div className="group relative overflow-hidden border border-line">
                      <img
                        src={c.img}
                        alt={c.alt}
                        loading="lazy"
                        className="clip-frame h-64 w-full object-cover grayscale-[30%] transition-transform duration-700 group-hover:scale-105 sm:h-72"
                      />
                      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent" />
                    </div>
                  )}
                </FadeUp>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
